import React, { useCallback, useRef, useState } from 'react';
import {
  createFeature,
  Show,
  Trans,
  useBrand,
  useTrackElement,
  useTranslation,
  withErrorBoundary,
} from '@edreamsodigeo/frontend-core';
import {
  BackgroundImage,
  Body,
  Box,
  Checkbox as PrismaCheckbox,
  CheckboxField,
  CheckboxLabel,
  Flex,
  Heading,
} from '@edreamsodigeo/prisma-design-system';

import { useLogin } from '~clientState/providers/LoginProvider';
import {
  useBrandColor,
  useHotelDealsMobileCardTracking,
  useOnMount,
  useOnVisible,
} from '~clientState/hooks';
import BrandLogo from '~shared/BrandLogo';
import DoublePill from '~shared/DoublePill';
import HighlightText from '~shared/HighlightText';
import LocationPill from '~shared/LocationPill';
import { XSELL_MKT_CAMPAIGN_MOBILE } from '~clientState/ab';
import type { debugSegmentType } from '~clientState/hooks/useAdaptiveTranslate/useTranslateWithSegment';
import useTranslateWithSegment from '~clientState/hooks/useAdaptiveTranslate/useTranslateWithSegment';
import useCampaignTranslate from '~touchpoints/AccommodationDeals/hooks/useCampaignTranslate/useCampaignTranslate';
import { withHotelsEnabled, withModuleContext } from '~hocs';
import PixelTracking from '~shared/PixelTracking';
import type { CheckoutSegment } from '../../types';
import useGoogleAnalyticsTracking from '../FlightSummaryHotelsMap/hooks/useGoogleAnalyticsTracking';
import useDatameshTracking from '../FlightSummaryHotelsMap/hooks/useDatameshTracking';

// nice to have: use picture tag with both 1x/2x imgs
// x1 img: "/images/tests/flight_summary/flight_summary.png"
const HOTEL_ROOM_IMG_URL = '/images/tests/flight_summary/flight_summary@2x.png';
const FALLBACK_SAVINGS = '270€';

const parseLocationPillText = (
  amountNights: number,
  destinationName: string,
  t: (
    code: string,
    params: { amountNights: number; destinationName: string }
  ) => string
) => {
  if (!destinationName) {
    return null;
  }

  if (amountNights === 1) {
    return t('hoteldeals.nights.singular', { amountNights, destinationName });
  }

  if (amountNights > 1) {
    return t('hoteldeals.nights.plural', { amountNights, destinationName });
  }

  return null;
};

export interface HotelDealsMobileCardProps {
  amountNights?: number;
  destinationName?: string;
  isCampaign?: boolean;
  segments: CheckoutSegment[];
  debugSegment?: debugSegmentType;
  isRoundTrip?: boolean;
}

const HotelDealsMobileCard = ({
  amountNights = 0,
  destinationName = '',
  isCampaign = false,
  debugSegment,
  isRoundTrip,
}: HotelDealsMobileCardProps) => {
  const { trackLoad, trackCheckboxClick, trackVisible } =
    useHotelDealsMobileCardTracking();
  const { trackOnLoad: trackingForAPartition } = useGoogleAnalyticsTracking();
  const { useDatameshTrackLoad } = useDatameshTracking();
  const datameshTrackOnloadControl = useDatameshTrackLoad();

  const [showHotelsChecked, setShowHotelsChecked] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const { t } = useTranslation();
  const { isPrime } = useLogin();
  const brand = useBrand();
  const { backgroundColor } = useBrandColor({ brand, isPrime });
  const locationPillText = parseLocationPillText(
    amountNights,
    destinationName,
    t
  );
  const elementRef = useRef(null);

  const shouldRender = isRoundTrip;

  const { translateWithSegment } = useTranslateWithSegment(debugSegment);
  const { getKeyWithCampaign, translateWithCampaign } = useCampaignTranslate();

  const titleLabelKey: string = isCampaign
    ? getKeyWithCampaign('hoteldeals.title.default')
    : translateWithSegment('hoteldeals.title.default').key;

  const translateFunction = isCampaign ? translateWithCampaign : t;

  const onMountCallback = useCallback(() => {
    if (shouldRender) {
      trackLoad();
      trackingForAPartition();
    }
  }, [shouldRender, trackLoad, trackingForAPartition]);

  const onVisibleCallback = useCallback(() => {
    trackVisible();
    setIsVisible(true);
  }, [trackVisible]);

  useOnMount(onMountCallback);
  useOnVisible({
    callback: onVisibleCallback,
    targetElementRef: elementRef,
  });

  const onCheckboxClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    trackCheckboxClick(isChecked);
    setShowHotelsChecked(isChecked);
  };

  const datameshTrackOnload = useTrackElement(
    ['display'],
    'hotel-banner-fl-summary_onload'
  );

  const datameshTrackClick = useTrackElement<HTMLInputElement>(
    ['click'],
    `hotel-banner-fl-summary_click:${showHotelsChecked ? 'uncheck' : 'check'}`
  );

  if (!shouldRender) {
    return null;
  }

  const TopSection = (
    <Box position="relative" sx={{ transform: 'translate(0, -12px)' }}>
      <Box maxWidth="160px" mb="minus_small">
        <Show when={Boolean(locationPillText)}>
          <LocationPill iconSize="tiny">
            <Body type="xSmall" sx={{ lineHeight: '12px' }}>
              {locationPillText}
            </Body>
          </LocationPill>
        </Show>
      </Box>
      <Box position="absolute" sx={{ top: 0, right: 0 }}>
        <DoublePill
          alignment="end"
          firstPill={{
            render: (
              <Heading type="6" weight="medium" color="primary.90">
                {t('hoteldeals.savings.text')}
              </Heading>
            ),
            sx: {
              borderRadius: '2px',
              boxShadow: '0px 2.21039px 8.84156px rgba(0, 0, 0, 0.23)',
              py: 'tiny',
              pr: 'base',
            },
          }}
          secondPill={{
            render: (
              <Heading type="3" weight="medium">
                <Heading type="3" weight="medium">
                  {translateFunction('hoteldeals.savings.amount') ||
                    FALLBACK_SAVINGS}
                </Heading>
              </Heading>
            ),
            sx: {
              borderRadius: '2px',
              boxShadow: '0px 2.21039px 13.2623px rgba(0, 0, 0, 0.1)',
              pr: 'base',
              borderTopLeftRadius: 0,
            },
          }}
        />
      </Box>
    </Box>
  );

  const Title = (
    <Box mt="base">
      <Heading
        color="neutral.0"
        type="3"
        weight="medium"
        sx={{ minHeight: '48px' }}
      >
        <Trans
          i18nKey={titleLabelKey}
          values={{
            highlight: t('hoteldeals.hoteltext'),
          }}
          components={{
            highlight: <HighlightText isPrime={isPrime} />,
          }}
        />
      </Heading>
    </Box>
  );

  const Checkbox = (
    <Box mt="base">
      <CheckboxField>
        <PrismaCheckbox
          checked={showHotelsChecked}
          onChange={onCheckboxClick}
          ref={datameshTrackClick}
          sx={{
            backgroundColor: 'transparent',
            borderColor: 'neutral.0',
            color: 'neutral.0',
          }}
          hasError // workaround to remove both :hover and :focus styles
        />
        <CheckboxLabel sx={{ color: 'neutral.0' }}>
          {t('hoteldeals.checkbox.default')}
        </CheckboxLabel>
      </CheckboxField>
    </Box>
  );

  return (
    <>
      <Box py="base" ref={elementRef}>
        <Box
          backgroundColor={backgroundColor}
          mt={locationPillText ? 'small' : 'big'}
          ref={datameshTrackOnload}
        >
          {TopSection}
          <Flex
            overflowX="hidden"
            justifyContent="space-between"
            ref={datameshTrackOnloadControl}
          >
            <Flex flexDirection="column" minWidth="70%" my="base" ml="base">
              <Flex sx={{ marginRight: 'auto' }}>
                <BrandLogo
                  width="auto"
                  height="16px"
                  type={isPrime ? 'primeLong' : 'short'}
                  theme="negative"
                />
              </Flex>
              {Title}
              {Checkbox}
            </Flex>
            <Flex flexBasis="100px" justifyContent="flex-end" flexShrink={0}>
              <BackgroundImage
                backgroundImage={`url("${HOTEL_ROOM_IMG_URL}")`}
                sx={{
                  backgroundPosition: 'bottom right',
                }}
                alt="Image of a hotel bed"
              />
            </Flex>
          </Flex>
        </Box>
      </Box>
      {isVisible && (
        <PixelTracking datameshId="hotel-banner-fl-summary_visible" />
      )}
    </>
  );
};

const HotelDealsMobileCardCampaign = (props: HotelDealsMobileCardProps) => (
  <HotelDealsMobileCard isCampaign {...props} />
);

const VersionedHotelDealsMobileCard = createFeature({
  name: XSELL_MKT_CAMPAIGN_MOBILE,
  variations: [HotelDealsMobileCard, HotelDealsMobileCardCampaign],
  version: 1,
});

export default withModuleContext(
  withErrorBoundary(
    withHotelsEnabled({
      Component: VersionedHotelDealsMobileCard,
      FallbackComponent: () => null,
      options: { onlyPrimeSites: true },
    }),
    {
      name: 'HotelDealsMobileCard',
    }
  )
);
